import _ from 'lodash';
import { CsvFileSummary } from '../../../../types';
import { Monitor } from '../../types';
import {
  AffectedHrsType,
  CriteriaType,
  DisplayMetadata,
  DurationType,
  IssueCriteria,
  LocationAttributeFilterPayload,
  LocationType,
  MonitorApiPayload,
  Operator,
  SubclauseType,
  TimeType,
} from './types';
import {
  CriteriaDisplayWithValue,
  IssueFilterGroup,
} from './IssueFilterConfig/types';
import { getUuid } from '../../utils/monitorUtils';

export const includeExcludeSequenceMarker = 'INCLUDEEXCLUDE';

export const makePayload = (
  data: AffectedHrsType | DurationType | LocationType,
): SubclauseType => {
  let payload: SubclauseType = {
    criteriaType: data.criteriaType,
    operator: data.operator?.key,
    selectedValue: 0,
  };
  if (typeof data.value !== 'number' && typeof data.value !== 'string') {
    payload.selectedValue = {
      range: data.value.range,
      minutes: data.value.minutes + data.value.hours * 60,
    };
  } else {
    payload.selectedValue = data.value;
  }
  return payload;
};

export const parseCriteriasToSequence = (
  groups: IssueFilterGroup[],
): string => {
  let sequence = ``;
  const filteredGroups = groups.filter((g) => g.items.length > 0);

  if (filteredGroups.length === 0) return sequence;

  const groupSequences = filteredGroups.map((group) => {
    const groupSequence = `${group.operator}(${group.items
      .map((item) => item.key)
      .join(',')})`;
    return {
      sequence: groupSequence,
      operator: group.externalOperator,
    };
  });

  for (let i = 0; i < groupSequences.length; i++) {
    if (i === 0) {
      sequence = groupSequences[i].sequence;
    } else {
      sequence = `${groupSequences[i - 1].operator}(${sequence}, ${groupSequences[i].sequence
        })`;
    }
  }
  return sequence;
};

export const finalizeMonitorAPIPayload = (
  currentPayload: MonitorApiPayload,
  includeExcludeList?: CsvFileSummary,
): MonitorApiPayload => {
  let newPayload: MonitorApiPayload = {
    name: currentPayload.name,
    criteria: {
      sequence: currentPayload.criteria!.sequence,
      criterias: {},
    },
    harvestSyncEnabled: currentPayload.harvestSyncEnabled,
    locationAttributes: {
      operator: Operator.AND,
      filters: [],
    },
  };
  Object.keys(currentPayload.criteria!.criterias).forEach((key) => {
    const {
      operator,
      criteriaType,
      selectedValue,
      subCriterias,
    } = currentPayload.criteria!.criterias[key];
    newPayload.criteria!.criterias[key] = {
      operator,
      criteriaType,
      selectedValue,
      ...(subCriterias && { subCriterias: subCriterias }),
    };
  });
  newPayload.locationAttributes = currentPayload.locationAttributes?.filters
    .length
    ? currentPayload.locationAttributes
    : undefined;

  if (newPayload.locationAttributes)
    newPayload.locationAttributes.operator = newPayload.locationAttributes?.operator.toLowerCase() as Operator;

  if (
    includeExcludeList &&
    includeExcludeList.file_id &&
    includeExcludeList.criteriaType
  ) {
    newPayload = {
      ...newPayload,
      locationAttributes: undefined,
      criteria: {
        sequence: newPayload.criteria!.sequence.includes(
          includeExcludeSequenceMarker,
        )
          ? newPayload.criteria!.sequence
          : `AND(${includeExcludeSequenceMarker}, ${newPayload.criteria!.sequence
          })`,
        criterias: {
          ...newPayload.criteria!.criterias,
          [`${includeExcludeSequenceMarker}`]: {
            operator: '=',
            criteriaType:
              includeExcludeList.criteriaType === 'include'
                ? CriteriaType.IncludeList
                : CriteriaType.ExcludeList,
            selectedValue: includeExcludeList.file_id,
          },
        },
      },
    };
  }

  Object.keys(newPayload).forEach(
    (key) =>
      newPayload[key as keyof MonitorApiPayload] === undefined &&
      delete newPayload[key as keyof MonitorApiPayload],
  );

  return newPayload;
};

export const verifyNumberInput = (
  input: any,
  max: number,
  min?: number,
): number => {
  let inputValue = parseFloat(input);
  if (_.isNumber(inputValue)) {
    inputValue = Math.min(max, Math.max(min || 0, inputValue));
    return inputValue;
  }
  return 0;
};

export const monitorContainsIncludeExclude = (payload: Monitor): boolean => {
  return Object.keys(
    payload?.criteria?.criterias || { operator: 'or', filters: [] },
  ).includes(includeExcludeSequenceMarker);
};

export const specificIssueCriteriaByTypeFromMetadata = (
  criteriaType: 'affectedHrs' | 'duration' | string,
  metadata: DisplayMetadata,
): IssueCriteria =>
  metadata.specificIssue.allowedCriteria.find(
    (crit) => crit.criteriaName === criteriaType,
  )!;

export const mapMinutesToDaysHoursMinutes = (minutes: number): TimeType => ({
  range: '_duration_last_1d',
  days: Math.floor(minutes / 1440),
  hours: Math.floor((minutes % 1440) / 60),
  minutes: minutes % 60,
});

export const monitorCompatible = ({ criteria }: Monitor): boolean => {
  const sequence = criteria?.sequence;
  if (!sequence) return false;
  let res = true;
  const criteriaIdArray = Object.keys(criteria.criterias);
  criteriaIdArray.forEach((key) => {
    if (criteria.criterias[key].selectedValue === 'any_issue') res = false;
  });
  return res;
};

export const getOperatorFromSequenceString = (
  sequenceString: string | undefined,
): Operator => {
  if (!sequenceString) return Operator.AND;
  if (
    sequenceString.toUpperCase().lastIndexOf(Operator.OR) >
    sequenceString.toUpperCase().lastIndexOf(Operator.AND)
  )
    return Operator.OR;
  return Operator.AND;
};

export const parseMonitorToAPIPayload = (
  monitor: Monitor,
  compatibilityCallback?: (comp: boolean) => void,
): MonitorApiPayload => {
  const newPayload: MonitorApiPayload = {
    name: monitor.name,
    harvestSyncEnabled: Boolean(monitor.harvestSyncEnabled),
    criteria: {
      sequence: monitor.criteria!.sequence || '',
      criterias: monitor.criteria!.criterias,
    },
    locationAttributes: monitor.locationAttributes || {
      operator: Operator.AND,
      filters: [],
    },
  };

  const compatible = monitorCompatible(monitor);
  compatibilityCallback?.(compatible);
  if (!compatible && newPayload.criteria) {
    const criteriaIdArray = Object.keys(newPayload.criteria.criterias);
    criteriaIdArray.forEach((key) => {
      if (newPayload.criteria!.criterias[key].selectedValue === 'any_issue') {
        delete newPayload.criteria!.criterias[key];
      }
    });
  }
  return newPayload;
};

export const monitorAPIPayloadApplyCriteria = (
  groups: IssueFilterGroup[],
  existingPayload: MonitorApiPayload,
): MonitorApiPayload => {
  const groupsWithUUID = groups.map((group) => ({
    ...group,
    items: group.items.map((crit) => ({
      ...crit,
      key: getUuid(),
      selectedValue: crit.selectedSubItem || crit.selectedValue,
    })),
  }));

  let newCriterias: Record<string, CriteriaDisplayWithValue> = {};
  groupsWithUUID
    .flatMap((group) => group.items)
    .filter((crit) => crit.key !== undefined)
    .forEach((crit) => {
      newCriterias[crit.key] = crit;
    });

  return {
    ...existingPayload,
    criteria: {
      sequence: parseCriteriasToSequence(groupsWithUUID),
      criterias: newCriterias,
    },
  };
};

export const isLocationFilterEmpty = (
  criteria: LocationAttributeFilterPayload,
): boolean => {
  if (criteria.columnValue === undefined || criteria.columnValue === null)
    return true;
  if (typeof criteria.columnValue === 'string')
    return criteria.columnValue.length === 0;
  if (typeof criteria.columnValue === 'number') return false;
  if (Array.isArray(criteria.columnValue))
    return (criteria.columnValue as string[]).length === 0;
  return false;
};
